import imagesService from "@/api/images.service";
import { CareerProps, ImageProps } from "@/types";
import { tokens } from "@/utilities";
import { Box, Button, InputLabel, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import defaultLogo from "@/assets/imagen.jpg";
import Swal from "sweetalert2";

export const DEFAULT_FALLBACK_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/guia-universitaria-dev.appspot.com/o/ies%2Fdefault%2Fimagen.jpg?alt=media&token=a8a9fa5f-c879-426b-939a-6dc80f80c11a";

export const DEFAULT_CAREER_IMAGE: ImageProps = {
  url: DEFAULT_FALLBACK_IMAGE_URL,
  alt: "Imagen institución por carrera",
};

export const resolveDefaultCareerImage = (
  career?: Partial<CareerProps> | null,
  currentImages?: ImageProps[]
): ImageProps => {
  if (career?.defaultImage?.url && career.defaultImage.url.trim() !== "") {
    return career.defaultImage;
  }

  const images =
    currentImages && currentImages.length > 0
      ? currentImages
      : career?.iesImages || [];

  if (images.length > 0 && images[0]?.url && images[0].url.trim() !== "") {
    return images[0];
  }

  return DEFAULT_CAREER_IMAGE;
};

interface Props {
  premiumPlan: string;
  selectedCareer: CareerProps;
  updateCareer: (careerMap: Partial<CareerProps>, id: string) => void;
  isEditable: boolean;
}

export const CareerImages = ({
  premiumPlan,
  selectedCareer,
  isEditable,
  updateCareer,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isEditingImages, setIsEditingImages] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const [downloadURLs, setDownloadURLs] = useState<ImageProps[]>(
    selectedCareer.iesImages || []
  );

  const [defaultImage, setDefaultImage] = useState<ImageProps>(() =>
    resolveDefaultCareerImage(selectedCareer, selectedCareer.iesImages)
  );

  useEffect(() => {
    if (!isEditingImages) {
      setDownloadURLs(selectedCareer.iesImages || []);
      setDefaultImage(resolveDefaultCareerImage(selectedCareer, selectedCareer.iesImages));
    }
  }, [selectedCareer, isEditingImages]);

  const handleToggleEdit = () => {
    if (!isEditingImages) {
      setDefaultImage((prev) => {
        if (prev?.url && prev.url.trim() !== "") {
          return prev;
        }
        return resolveDefaultCareerImage(selectedCareer, downloadURLs);
      });
    }
    setIsEditingImages(!isEditingImages);
  };

  const saveImages = async () => {
    try {
      if (selectedCareer.id && downloadURLs.length <= 5) {
        const finalDefaultImage =
          defaultImage?.url && defaultImage.url.trim() !== ""
            ? defaultImage
            : resolveDefaultCareerImage(selectedCareer, downloadURLs);

        await imagesService.updateImagesByCareerFirestore(
          selectedCareer.uidInstitution,
          selectedCareer.uidFaculty,
          selectedCareer.id,
          [...downloadURLs],
          finalDefaultImage
        );
        updateCareer({ iesImages: downloadURLs, defaultImage: finalDefaultImage }, selectedCareer.id);

        Swal.fire({
          title: "Los cambios se han guardado exitosamente",
          icon: "success",
          timer: 2000,
        });
        setIsEditingImages(false);
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      Swal.fire({
        title: "Error cargando las imágenes. Por favor, intente nuevamente.",
        icon: "error",
        timer: 2000,
      });
    }
  };

  async function handleImagesSubmit() {
    if (selectedImages.length === 0) {
      Swal.fire({
        title: "Por favor, selecciona imagenes",
        icon: "info",
        timer: 2000,
      });

      return;
    }
    try {
      if (selectedCareer.id) {
        const uploadedURLs = await imagesService.uploadCareersImages(
          selectedCareer.uidInstitution,
          selectedImages,
          selectedCareer.uidFaculty,
          selectedCareer.id
        );
        const newDownloadURLs = [...downloadURLs, ...uploadedURLs];
        setDownloadURLs(newDownloadURLs);
        setSelectedImages([]);
        setDefaultImage((prev) => {
          if (prev?.url && prev.url.trim() !== "" && prev.url !== DEFAULT_FALLBACK_IMAGE_URL) {
            return prev;
          }
          return newDownloadURLs.length > 0 && newDownloadURLs[0]?.url
            ? newDownloadURLs[0]
            : DEFAULT_CAREER_IMAGE;
        });
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      Swal.fire({
        title: "Error cargando las imágenes. Por favor, intente nuevamente.",
        icon: "error",
        timer: 2000,
      });
    }
  }

  const handleImagesChange = (event: React.ChangeEvent<any>) => {
    const inputElement = event.target as HTMLInputElement;

    if (inputElement.files && inputElement.files.length > 5) {
      Swal.fire({
        title: "Selecciona un máximo de 5 archivos.",
        icon: "info",
        timer: 2000,
      });
      event.preventDefault();
      return;
    }

    if (inputElement.files) {
      const selectedFiles = Array.from(inputElement.files);
      setSelectedImages(selectedFiles);
    }
  };

  const handleDeleteImages = async (index: number) => {
    try {
      const deletedImage = downloadURLs[index];
      const updatedImages = downloadURLs.filter((_, i) => i !== index);
      setDownloadURLs(updatedImages);
      if (defaultImage?.url === deletedImage?.url) {
        setDefaultImage(
          updatedImages.length > 0 && updatedImages[0]?.url
            ? updatedImages[0]
            : DEFAULT_CAREER_IMAGE
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        position: "relative",
        gap: "0 20px",
      }}
    >
      {isEditingImages ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: "0" },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            disabled={downloadURLs.length > 5}
            onClick={saveImages}
          >
            Guardar
          </Button>
          {!isEditable && (
            <Typography variant="h5" sx={{ margin: "0", color: "red" }}>
              {premiumPlan}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: "0" },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            onClick={handleToggleEdit}
          >
            Editar
          </Button>
        </Box>
      )}

      <Typography
        sx={{
          padding: "20px 0",
          color: colors.grey[100],
          marginTop: "10px",
        }}
        variant="h3"
      >
        Imágenes
      </Typography>

      {downloadURLs.length > 5 && (
        <Typography
          sx={{
            marginTop: "35px",
            color: "red",
            display: "flex",
          }}
          variant="h6"
        >
          Nota: Solo se permite un máximo de 5 imágenes por carrera.
        </Typography>
      )}

      <hr style={{ width: "100%", marginBottom: "25px" }} />

      {downloadURLs.length > 0 ? (
        downloadURLs.map((image, index) => (
          <Box key={index} sx={{ width: "fit-content", position: "relative" }}>
            <img
              src={image.url}
              alt={image.title}
              style={{
                display: "flex",
                flexWrap: "wrap",
                margin: "15px 0px",
                minHeight: "200px",
                width: "250px",
                maxWidth: "300px",
                maxHeight: "200px",
                borderRadius: "20px",
                marginBottom: "40px",
                objectFit: "cover",
                border:
                  isEditingImages && defaultImage?.url === image.url
                    ? "3px solid #007FC0"
                    : "3px solid transparent",
                cursor: isEditingImages ? "pointer" : "default",
              }}
              onClick={() => {
                if (isEditingImages) {
                  setDefaultImage(image);
                }
              }}
            />
            {isEditingImages ? (
              <Button
                sx={{
                  position: "absolute",
                  left: 0,
                  top: -10,
                  padding: 0,
                  border: "none",
                }}
                variant="outlined"
                onClick={() => handleDeleteImages(index)}
              >
                Eliminar
              </Button>
            ) : (
              ""
            )}
          </Box>
        ))
      ) : (
        <Box sx={{ width: "fit-content", position: "relative" }}>
          <img
            src={DEFAULT_FALLBACK_IMAGE_URL}
            alt="Logo Guía Universitaria"
            style={{
              display: "flex",
              flexWrap: "wrap",
              margin: "15px 0px",
              minHeight: "200px",
              width: "250px",
              maxWidth: "300px",
              maxHeight: "200px",
              borderRadius: "20px",
              marginBottom: "40px",
              objectFit: "cover",
            }}
          />
          {/* { isEditingImages? <Button  sx={{position:"absolute", left:0, top:-10,  padding:0, border:"none"}} variant="outlined" onClick={() => handleDeleteImages(0)}>Eliminar</Button>
            :""
              } */}
        </Box>
      )}

      {isEditingImages && downloadURLs.length < 5 ? (
        <Box>
          <InputLabel id="iesImages">Añadir imagenes de la carrera</InputLabel>
          <input
            type="file"
            id="iesImages"
            name="iesImages"
            onChange={handleImagesChange}
            accept="image/png, image/jpeg, image/webp"
            multiple
          />
          <Button variant="outlined" onClick={handleImagesSubmit}>
            Subir
          </Button>
          {/* <img src={url || ''} alt='firebase-img'/>  */}
        </Box>
      ) : (
        ""
      )}

      {isEditingImages && (
        <Box sx={{ width: "100%" }}>
          <Typography variant="h4" sx={{ color: colors.grey[100] }}>
            Imagen predeterminada
          </Typography>
          <img
            src={
              defaultImage && defaultImage.url ? defaultImage.url : defaultLogo
            }
            alt="Logo Guía Universitaria"
            style={{
              display: "flex",
              flexWrap: "wrap",
              margin: "15px 0px",
              minHeight: "200px",
              width: "250px",
              maxWidth: "300px",
              maxHeight: "200px",
              borderRadius: "20px",
              marginBottom: "40px",
              objectFit: "cover",
            }}
          />
        </Box>
      )}
    </Box>
  );
};
